import type { Complex } from "./complex";

/** Axis-aligned window on the complex plane. `width` is the real-axis span. */
export type View = {
  center: Complex;
  width: number;
};

export const MANDEL_VIEW: View = { center: { re: -0.5, im: 0 }, width: 3.2 };
export const JULIA_VIEW: View = { center: { re: 0, im: 0 }, width: 3.5 };

export function cloneView(v: View): View {
  return { center: { re: v.center.re, im: v.center.im }, width: v.width };
}

export function planeHeight(view: View, aspect: number): number {
  if (aspect <= 0) return view.width;
  return view.width / aspect;
}

export function pixelToComplex(
  px: number,
  py: number,
  cssW: number,
  cssH: number,
  view: View,
): Complex {
  const aspect = cssW / Math.max(cssH, 1e-9);
  const h = planeHeight(view, aspect);
  return {
    re: view.center.re + (px / cssW - 0.5) * view.width,
    im: view.center.im - (py / cssH - 0.5) * h,
  };
}

export function complexToPixel(
  z: Complex,
  cssW: number,
  cssH: number,
  view: View,
): { x: number; y: number } {
  const aspect = cssW / Math.max(cssH, 1e-9);
  const h = planeHeight(view, aspect);
  return {
    x: ((z.re - view.center.re) / view.width + 0.5) * cssW,
    y: (0.5 - (z.im - view.center.im) / h) * cssH,
  };
}

export function zoomAt(view: View, focus: Complex, factor: number): View {
  const width = Math.min(8, Math.max(1e-5, view.width * factor));
  const t = 1 - width / view.width;
  return {
    width,
    center: {
      re: view.center.re + (focus.re - view.center.re) * t,
      im: view.center.im + (focus.im - view.center.im) * t,
    },
  };
}

export function panBy(view: View, dRe: number, dIm: number): View {
  return {
    width: view.width,
    center: { re: view.center.re + dRe, im: view.center.im + dIm },
  };
}

/** Widen the view just enough that `z` sits inside with a margin. */
export function framePoint(view: View, z: Complex, aspect: number, margin = 0.18): View {
  const h = planeHeight(view, aspect);
  const dx = Math.abs(z.re - view.center.re);
  const dy = Math.abs(z.im - view.center.im);
  const needW = Math.max(view.width, (dx * 2) / (1 - 2 * margin));
  const needH = Math.max(h, (dy * 2) / (1 - 2 * margin));
  const width = Math.max(needW, needH * aspect);
  return { center: { ...view.center }, width };
}
